import type { TripData } from "./bookings-api";

const US_SELF_DRIVE_TYPES = new Set(["driving", "self-driving", "self_drive", "self-drive", "selfdrive"]);

/** Renter and host may view; management remains renter-only. */
export function canShowAdditionalDriverRoster(
  trip: Pick<TripData, "isRenter" | "isOwner" | "status" | "rental_type" | "cars"> | null | undefined,
): boolean {
  if (!trip || (!trip.isRenter && !trip.isOwner)) return false;
  if (String(trip.cars?.country_code ?? "").trim().toUpperCase() !== "US") return false;
  if (!US_SELF_DRIVE_TYPES.has(String(trip.rental_type ?? "").trim().toLowerCase())) return false;
  return ["confirmed", "trip_confirmed"].includes(String(trip.status ?? "").trim().toLowerCase());
}

/** A failed refresh must not retain a stale positive clearance after a change. */
export function driverRosterUnavailable(trip: TripData | null): TripData | null {
  if (!canShowAdditionalDriverRoster(trip) || !trip) return trip;
  return {
    ...trip,
    primaryDriver: trip.primaryDriver ? { ...trip.primaryDriver, clearance: "unknown", identity: null } : trip.primaryDriver,
    additionalDriverRoster: null,
    verificationSummary: trip.verificationSummary ? { ...trip.verificationSummary, action: "none" } : null,
  };
}

/** Mirrors the additional-driver endpoint's strict more-than-two-hours cutoff. */
export function isAdditionalDriverInviteWindowOpen(
  pickupAt: string | null | undefined,
  nowMs = Date.now(),
): boolean {
  if (!pickupAt) return false;
  const pickupMs = Date.parse(pickupAt);
  return Number.isFinite(pickupMs) && pickupMs > nowMs + 2 * 60 * 60 * 1000;
}

export function additionalDriverStatusKey(driver: {
  status: string;
  clearance: string;
}): string {
  // Inactive invitations cannot be approved by a stale clearance field.
  switch (driver.status.toLowerCase()) {
    case "declined": return "booking.additional_drivers.status_declined";
    case "expired": return "booking.additional_drivers.status_expired";
    case "revoked":
    case "removed": return "booking.additional_drivers.status_revoked";
    case "rejected": return "booking.additional_drivers.status_rejected";
  }
  if (driver.clearance === "unknown") return "booking.additional_drivers.status_unknown";
  if (driver.clearance === "rejected") return "booking.additional_drivers.status_rejected";
  if (driver.clearance === "in_review") return "booking.additional_drivers.status_in_review";
  if (driver.clearance === "approved" && ["confirmed", "renter"].includes(driver.status.toLowerCase())) {
    return "booking.additional_drivers.status_cleared";
  }
  if (driver.clearance === "approved") return "booking.additional_drivers.status_verification_pending";
  if (driver.clearance === "verification_pending") return "booking.additional_drivers.status_verification_pending";
  if (driver.clearance === "verification_required") return "booking.additional_drivers.status_verification_required";
  if (driver.clearance === "invitation_pending") return "booking.additional_drivers.status_invitation_pending";
  switch (driver.status.toLowerCase()) {
    case "declined":
      return "booking.additional_drivers.status_declined";
    case "expired":
      return "booking.additional_drivers.status_expired";
    case "revoked":
      return "booking.additional_drivers.status_revoked";
    default:
      return "booking.additional_drivers.status_inactive";
  }
}

export function isRosterDriverApproved(driver: { status: string; clearance?: string }): boolean {
  return additionalDriverStatusKey({ status: driver.status, clearance: driver.clearance ?? "unknown" }) ===
    "booking.additional_drivers.status_cleared";
}

/** Stable grouping; preserve source ordering amongst people with the same status. */
export function driverRosterStatusOrder(driver: { status: string; clearance: string }): number {
  const keys = [
    "status_cleared", "status_in_review", "status_verification_pending",
    "status_verification_required", "status_invitation_pending", "status_unknown",
    "status_rejected", "status_declined", "status_expired", "status_revoked", "status_inactive",
  ];
  return keys.indexOf(additionalDriverStatusKey(driver).replace("booking.additional_drivers.", ""));
}